import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Users, Maximize, Bed, Eye, Wifi, Coffee, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, addDays, differenceInDays } from 'date-fns';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const amenityIcons = {
  wifi: Wifi,
  minibar: Coffee,
  jacuzzi: Sparkles,
  butler: Sparkles,
  breakfast: Coffee,
  balcony: Eye,
  private_pool: Sparkles,
  chef: Coffee,
  beach_access: Eye,
};

export const RoomDetail = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(2);
  const [unavailableDates, setUnavailableDates] = useState([]);

  useEffect(() => {
    const fetchRoom = async () => {
      setLoading(true);
      try {
        const roomRes = await axios.get(`${API}/rooms/${roomId}`);
        setRoom(roomRes.data);
        
        // Fetch unavailable dates separately to avoid blocking
        try {
          const datesRes = await axios.get(`${API}/rooms/${roomId}/unavailable-dates`);
          setUnavailableDates(datesRes.data.unavailable_dates.map((d) => new Date(d)));
        } catch (dateError) {
          console.warn('Could not fetch unavailable dates:', dateError);
          setUnavailableDates([]);
        }
      } catch (error) {
        console.error('Error fetching room:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRoom();
  }, [roomId]);

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;
  const totalPrice = room ? room.price_per_night * nights : 0;

  const handleBook = () => {
    if (checkIn && checkOut) {
      navigate(`/booking/${roomId}?checkIn=${checkIn.toISOString()}&checkOut=${checkOut.toISOString()}&guests=${guests}`);
    }
  };

  const isDateUnavailable = (date) => {
    return unavailableDates.some(
      (d) => d.toDateString() === date.toDateString()
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-20 flex items-center justify-center">
        <p className="text-[#64748B]">Room not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="room-detail-page">
      {/* Image Gallery */}
      <section className="relative h-[70vh] min-h-[500px]">
        <div className="absolute inset-0">
          <img
            src={room.images[currentImage]}
            alt={room.name[language] || room.name.en}
            className="w-full h-full object-cover transition-opacity duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1F3C]/30 via-transparent to-[#0F1F3C]/50" />
        </div>

        {/* Gallery Controls */}
        {room.images.length > 1 && (
          <>
            <button
              onClick={() => setCurrentImage((prev) => (prev === 0 ? room.images.length - 1 : prev - 1))}
              className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
              data-testid="gallery-prev"
            >
              <ChevronLeft className="w-5 h-5 text-[#0F1F3C]" />
            </button>
            <button
              onClick={() => setCurrentImage((prev) => (prev === room.images.length - 1 ? 0 : prev + 1))}
              className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
              data-testid="gallery-next"
            >
              <ChevronRight className="w-5 h-5 text-[#0F1F3C]" />
            </button>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2">
              {room.images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`w-2 h-2 transition-colors ${
                    currentImage === index ? 'bg-[#D4AF37]' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Details */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-sans font-medium mb-4">
                  {room.view}
                </p>
                <h1 className="font-serif text-4xl lg:text-5xl text-[#0F1F3C] mb-6">
                  {room.name[language] || room.name.en}
                </h1>
                <p className="text-[#64748B] font-sans leading-relaxed text-lg mb-8">
                  {room.description[language] || room.description.en}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-8 py-8 border-t border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <Maximize className="w-5 h-5 text-[#D4AF37]" />
                    <span className="font-sans text-[#0F1F3C]">{room.size_sqm} {t('rooms.sqm')}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Users className="w-5 h-5 text-[#D4AF37]" />
                    <span className="font-sans text-[#0F1F3C]">{t('rooms.maxGuests')}: {room.max_guests}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Bed className="w-5 h-5 text-[#D4AF37]" />
                    <span className="font-sans text-[#0F1F3C]">{room.bed_type}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Eye className="w-5 h-5 text-[#D4AF37]" />
                    <span className="font-sans text-[#0F1F3C]">{room.view}</span>
                  </div>
                </div>

                {/* Amenities */}
                <div className="py-8">
                  <h3 className="font-serif text-xl text-[#0F1F3C] mb-6">{t('rooms.amenities')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {room.amenities.map((amenity) => {
                      const Icon = amenityIcons[amenity] || Sparkles;
                      return (
                        <div key={amenity} className="flex items-center space-x-3">
                          <Icon className="w-4 h-4 text-[#D4AF37]" />
                          <span className="font-sans text-sm text-[#0F1F3C] capitalize">
                            {amenity.replace('_', ' ')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Booking Widget */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white p-8 shadow-[0_20px_40px_-15px_rgba(15,31,60,0.1)] sticky top-28"
                data-testid="room-booking-widget"
              >
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-wider text-[#64748B] font-sans">{t('rooms.from')}</p>
                  <p className="font-serif text-4xl text-[#0F1F3C]">
                    €{room.price_per_night}
                    <span className="text-lg text-[#64748B] font-sans">/{t('booking.night')}</span>
                  </p>
                </div>

                {/* Check-in */}
                <div className="mb-4">
                  <label className="text-xs uppercase tracking-[0.15em] text-[#0F1F3C]/60 font-sans font-medium mb-2 block">
                    {t('booking.checkIn')}
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className="w-full flex items-center justify-between border border-gray-200 p-3 text-[#0F1F3C] hover:border-[#0F1F3C] transition-colors"
                        data-testid="room-checkin-picker"
                      >
                        <span className="font-sans">
                          {checkIn ? format(checkIn, 'MMM dd, yyyy') : t('booking.selectDates')}
                        </span>
                        <Calendar className="w-4 h-4 text-[#D4AF37]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={checkIn}
                        onSelect={(date) => {
                          setCheckIn(date);
                          if (!checkOut || (date && checkOut <= date)) {
                            setCheckOut(addDays(date, 1));
                          }
                        }}
                        disabled={(date) => date < new Date() || isDateUnavailable(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Check-out */}
                <div className="mb-4">
                  <label className="text-xs uppercase tracking-[0.15em] text-[#0F1F3C]/60 font-sans font-medium mb-2 block">
                    {t('booking.checkOut')}
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className="w-full flex items-center justify-between border border-gray-200 p-3 text-[#0F1F3C] hover:border-[#0F1F3C] transition-colors"
                        data-testid="room-checkout-picker"
                      >
                        <span className="font-sans">
                          {checkOut ? format(checkOut, 'MMM dd, yyyy') : t('booking.selectDates')}
                        </span>
                        <Calendar className="w-4 h-4 text-[#D4AF37]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={checkOut}
                        onSelect={setCheckOut}
                        disabled={(date) => date <= (checkIn || new Date()) || isDateUnavailable(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Guests */}
                <div className="mb-6">
                  <label className="text-xs uppercase tracking-[0.15em] text-[#0F1F3C]/60 font-sans font-medium mb-2 block">
                    {t('booking.guests')}
                  </label>
                  <div className="flex items-center justify-between border border-gray-200 p-3">
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-[#D4AF37]" />
                      <span className="font-sans text-[#0F1F3C]">{guests}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setGuests(Math.max(1, guests - 1))}
                        className="w-8 h-8 border border-gray-200 flex items-center justify-center hover:border-[#0F1F3C] transition-colors"
                        data-testid="room-guests-decrease"
                      >
                        -
                      </button>
                      <button
                        onClick={() => setGuests(Math.min(room.max_guests, guests + 1))}
                        className="w-8 h-8 border border-gray-200 flex items-center justify-center hover:border-[#0F1F3C] transition-colors"
                        data-testid="room-guests-increase"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {/* Price Summary */}
                {nights > 0 && (
                  <div className="mb-6 py-4 border-t border-gray-100">
                    <div className="flex justify-between mb-2">
                      <span className="font-sans text-[#64748B]">
                        €{room.price_per_night} x {nights} {nights === 1 ? t('booking.night') : t('booking.nights')}
                      </span>
                      <span className="font-sans text-[#0F1F3C]">€{totalPrice}</span>
                    </div>
                    <div className="flex justify-between pt-4 border-t border-gray-100">
                      <span className="font-sans font-medium text-[#0F1F3C]">{t('booking.totalPrice')}</span>
                      <span className="font-serif text-xl text-[#0F1F3C]">€{totalPrice}</span>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleBook}
                  disabled={!checkIn || !checkOut}
                  className="w-full bg-[#0F1F3C] text-white hover:bg-[#1A2F4F] py-4 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="book-this-room-btn"
                >
                  {t('rooms.bookThisRoom')}
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
